import React, {FC, useMemo} from 'react';
import cn from 'classnames';
import {WeatherAlert, WeatherForecastHour} from '@/app/interfaces/weather';
import {getNowDateWidthOffsetToLocal} from '@/common/helpers/date/getNowDateWidthOffsetToLocal';
import {WeatherAlertItem} from '../WeatherAlertItem';
import styles from './WeatherAlerts.css';

interface WeatherHourlyAlertsProps {
	className?: string;
	itemClassName?: string;
	hours: WeatherForecastHour[];
	date?: string;
	fromCurrentHour: boolean;
	timezoneOffset: number;
}

export const WeatherAlerts: FC<WeatherHourlyAlertsProps> = props => {
	const {className, itemClassName, hours, date, fromCurrentHour, timezoneOffset} = props;

	// Calculates unique alerts relevant to the weather forecast for the specified day
	// If the current hour is specified, past alerts form past hours will be ignored
	const alerts = useMemo(() => {
		const list = hours.reduce<{from: number; to: number; alert: WeatherAlert}[]>((total, hour) => {
			if (!hour.alerts) {
				return total;
			}

			// Add new unique alerts into the list and set last actual hours
			for (const newAlert of hour.alerts) {
				const existItem = total.find(
					item => item.alert.type === newAlert.type && item.alert.severity === newAlert.severity,
				);
				if (existItem) {
					existItem.to = hour.hour + 1;
				} else {
					total.push({from: hour.hour, to: hour.hour + 1, alert: newAlert});
				}
			}

			return total;
		}, []);

		if (!fromCurrentHour) {
			return list;
		}

		// Filter past hours
		const currentHour = getNowDateWidthOffsetToLocal(timezoneOffset).getHours();
		return list.filter(item => item.to >= currentHour);
	}, [fromCurrentHour, timezoneOffset, hours]);

	if (alerts.length === 0) {
		return null;
	}

	return (
		<div className={className}>
			{alerts.map(item => (
				<WeatherAlertItem
					key={item.alert.type}
					className={cn(styles.entry, itemClassName)}
					alert={item.alert}
					date={date}
					fromHour={item.from}
					toHour={item.to}
				/>
			))}
		</div>
	);
};

WeatherAlerts.displayName = 'WeatherAlerts';
